import React, { useEffect, useState, useRef } from 'react';
import { XIcon, MessageSquareIcon, BotIcon, SendIcon } from 'lucide-react';
import { usePreventScroll } from '../../../hooks/usePreventScroll';
import { toast } from 'sonner';
import { ERROR_MESSAGES } from '../../../config/error-messages';
import { useErrorBoundary } from '../../../hooks/useErrorBoundary';
interface Props {
  isOpen: boolean;
  onClose: () => void;
  chatbot: {
    name: string;
    personality: string;
  };
}
interface Message {
  role: 'user' | 'bot';
  content: string;
  timestamp: string;
}
export const ChatbotPreviewModal = ({
  isOpen,
  onClose,
  chatbot
}: Props) => {
  const [{
    hasError,
    error
  }, handleError, resetError] = useErrorBoundary();
  const [messages, setMessages] = useState<Message[]>([{
    role: 'bot',
    content: `Hi! I'm ${chatbot.name}. How can I help you today?`,
    timestamp: new Date().toLocaleTimeString()
  }]);
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Prevent scroll on body when modal is open
  usePreventScroll(isOpen);
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);
  // Handle dynamic height and scrolling
  useEffect(() => {
    if (isOpen) {
      const updateHeight = () => {
        const vh = window.innerHeight;
        const modalPadding = window.innerWidth >= 640 ? 32 : 16;
        const headerHeight = 60;
        const inputHeight = 80;
        const maxContentHeight = vh - modalPadding * 2 - headerHeight - inputHeight;
        const chatContainer = document.querySelector('.chat-container') as HTMLElement;
        if (chatContainer) {
          chatContainer.style.maxHeight = `${maxContentHeight}px`;
        }
        // Scroll to bottom with a delay to ensure content is rendered
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth'
          });
        }, 100);
      };
      updateHeight();
      window.addEventListener('resize', updateHeight);
      inputRef.current?.focus();
      return () => {
        window.removeEventListener('resize', updateHeight);
      };
    }
  }, [isOpen, messages]);
  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    try {
      setIsProcessing(true);
      const newMessage: Message = {
        role: 'user',
        content: inputMessage,
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages(prev => [...prev, newMessage]);
      setInputMessage('');
      // Simulate API call with artificial delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      const botResponse: Message = {
        role: 'bot',
        content: `This is a preview response from ${chatbot.name}. In a real implementation, this would be processed through the AI model based on the knowledge space content.`,
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages(prev => [...prev, botResponse]);
    } catch (error) {
      handleError(error as Error);
      toast.error(ERROR_MESSAGES.api.serverError);
    } finally {
      setIsProcessing(false);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  if (hasError) {
    return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-798">
        <div className="bg-surface-light border border-surface-border rounded-none p-6 max-w-lg w-full" data-id="element-799">
          <div className="flex items-start gap-4" data-id="element-800">
            <div className="p-3 bg-status-error/10 rounded-none" data-id="element-801">
              <AlertCircleIcon size={24} className="text-status-error" data-id="element-802" />
            </div>
            <div className="flex-1" data-id="element-803">
              <h1 className="text-lg font-medium text-white font-mono mb-2" data-id="element-804">
                Chat Error
              </h1>
              <div className="bg-surface p-4 rounded-none border border-surface-border mb-4" data-id="element-805">
                <code className="text-sm text-gray-400 font-mono" data-id="element-806">
                  {error?.message || ERROR_MESSAGES.api.serverError}
                </code>
              </div>
              <div className="flex gap-3" data-id="element-807">
                <button onClick={resetError} className="flex items-center gap-2 px-4 py-2 bg-accent 
                           hover:bg-accent-light text-white rounded-none transition-colors" data-id="element-808">
                  <RefreshCwIcon size={16} data-id="element-809" />
                  <span data-id="element-810">Try Again</span>
                </button>
                <button onClick={onClose} className="px-4 py-2 border border-surface-border text-gray-400 
                           hover:text-white rounded-none transition-colors" data-id="element-811">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-812">
      <div className="bg-surface-light border border-surface-border rounded-none w-full max-w-2xl flex flex-col" data-id="element-813">
        {/* Header */}
        <div className="px-6 py-4 border-b border-surface-border flex items-center justify-between shrink-0" data-id="element-814">
          <div className="flex items-center gap-3" data-id="element-815">
            <MessageSquareIcon size={20} className="text-accent-light" data-id="element-816" />
            <h2 className="text-lg font-medium text-white font-mono" data-id="element-817">
              {chatbot.name} Preview
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300 transition-colors" data-id="element-818">
            <XIcon size={20} data-id="element-819" />
          </button>
        </div>
        {/* Chat Messages - Now with dynamic height */}
        <div className="chat-container overflow-y-auto p-6 space-y-6" data-id="element-820">
          {messages.map((message, index) => <div key={index} className={`flex items-start gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`} data-id="element-821">
              {message.role === 'bot' && <div className="w-8 h-8 rounded-none bg-accent/10 flex items-center justify-center shrink-0" data-id="element-822">
                  <BotIcon size={16} className="text-accent-light" data-id="element-823" />
                </div>}
              <div className={`
                  flex-1 max-w-[80%] p-4 rounded-none
                  ${message.role === 'user' ? 'bg-accent text-white' : 'bg-surface border border-surface-border text-gray-100'}
                `} data-id="element-824">
                <p className="text-sm whitespace-pre-wrap" data-id="element-825">{message.content}</p>
                <p className="text-xs mt-2 opacity-60" data-id="element-826">{message.timestamp}</p>
              </div>
            </div>)}
          <div ref={messagesEndRef} data-id="element-827" />
        </div>
        {/* Input Area - Now fixed at bottom */}
        <div className="border-t border-surface-border p-4 bg-surface shrink-0" data-id="element-828">
          <div className="flex items-end gap-3" data-id="element-829">
            <textarea ref={inputRef} value={inputMessage} onChange={e => setInputMessage(e.target.value)} onKeyPress={handleKeyPress} placeholder="Type your message..." disabled={isProcessing} className={`flex-1 px-4 py-2 bg-surface-light border border-surface-border rounded-none 
                   text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 
                   focus:ring-accent focus:border-transparent ${isProcessing ? 'opacity-50' : ''}`} data-id="element-830" />
            <button onClick={handleSendMessage} disabled={!inputMessage.trim() || isProcessing} className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-light text-white 
                   rounded-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-831">
              {isProcessing ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-832" /> : <SendIcon size={16} data-id="element-833" />}
              <span className="font-mono text-sm" data-id="element-834">Send</span>
            </button>
          </div>
        </div>
      </div>
    </div>;
};